import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  workspaceApi,
  type Invoice,
  type InvoiceStatus,
  type LineItem,
} from '../api/workspace';
import { EmptyState, Loading, PageError } from '../components/State';
import { Modal } from '../components/Modal';
import { isApiError } from '../lib/apiClient';
import { fmtDate, money, todayIso } from '../lib/format';
import './InvoicesPage.css';

type InvoiceWithYouth = Invoice & { youthName: string };
type Filter = 'all' | InvoiceStatus;

async function loadAllInvoices(): Promise<InvoiceWithYouth[]> {
  const clients = await workspaceApi.clients();
  const per = await Promise.all(
    clients.map(async (c) => {
      const list = await workspaceApi.invoices(c.connectionId).catch(() => [] as Invoice[]);
      const youthName = c.youthSnapshot?.name ?? 'Youth';
      return list.map((i) => ({ ...i, youthName }));
    }),
  );
  return per.flat().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function InvoicesPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const [showNew, setShowNew] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['invoices'],
    queryFn: loadAllInvoices,
  });

  const rows = data ?? [];
  const paidTotal = rows.filter((i) => i.status === 'paid').reduce((s, i) => s + i.totalAmount, 0);
  const pendingCount = rows.filter((i) => i.status === 'draft' || i.status === 'sent').length;
  const shown = filter === 'all' ? rows : rows.filter((i) => i.status === filter);
  const countFor = (f: Filter) =>
    f === 'all' ? rows.length : rows.filter((i) => i.status === f).length;

  if (isLoading) return <Loading />;

  return (
    <div>
      <div className="page-head">
        <h1>Invoices</h1>
        <button className="btn" onClick={() => setShowNew(true)}>
          New invoice
        </button>
      </div>

      {isError ? (
        <PageError error={error} onRetry={() => refetch()} />
      ) : (
        <>
          <div className="inv__summary">
            <div>
              <div className="inv__summary-val">{money(paidTotal)}</div>
              <div className="muted inv__summary-lbl">Paid</div>
            </div>
            <div>
              <div className="inv__summary-val">{pendingCount}</div>
              <div className="muted inv__summary-lbl">Draft or sent</div>
            </div>
          </div>

          <div className="tabbar">
            {(['all', 'draft', 'sent', 'paid', 'cancelled'] as Filter[]).map((f) => (
              <button
                key={f}
                className={`tabbtn${filter === f ? ' is-active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f[0].toUpperCase() + f.slice(1)} ({countFor(f)})
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <EmptyState text="No invoices here yet." />
          ) : (
            <div className="card inv__list">
              {shown.map((inv) => (
                <Link
                  key={inv.id}
                  to={`/invoices/${inv.id}`}
                  state={{ invoice: inv, youthName: inv.youthName, connectionId: inv.connectionId }}
                  className="inv__row"
                >
                  <div className="inv__row-main">
                    <div className="inv__row-num">{inv.invoiceNumber}</div>
                    <div className="muted inv__row-sub">
                      {inv.youthName}
                      {inv.dueDate ? ` · due ${fmtDate(inv.dueDate)}` : ''}
                    </div>
                  </div>
                  <StatusPill status={inv.status} />
                  <div className="inv__row-amt">{money(inv.totalAmount, inv.currency)}</div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}

      {showNew && <NewInvoiceModal onClose={() => setShowNew(false)} />}
    </div>
  );
}

export function StatusPill({ status }: { status: InvoiceStatus }) {
  return <span className={`inv__pill inv__pill--${status}`}>{status}</span>;
}

// ── New invoice modal ─────────────────────────────────────────────────────────

const blankItem = (): LineItem => ({ description: '', quantity: 1, unitPrice: 0 });

function NewInvoiceModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const clients = useQuery({ queryKey: ['clients'], queryFn: workspaceApi.clients });

  const [connectionId, setConnectionId] = useState('');
  const [items, setItems] = useState<LineItem[]>([blankItem()]);
  const [currency, setCurrency] = useState('CAD');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  const total = items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);

  const create = useMutation({
    mutationFn: () =>
      workspaceApi.createInvoice(connectionId, {
        lineItems: items.map((i) => ({
          description: i.description.trim(),
          quantity: i.quantity,
          unitPrice: i.unitPrice,
        })),
        currency,
        dueDate: dueDate || undefined,
        notes: notes.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
    },
  });

  const valid =
    connectionId &&
    items.length > 0 &&
    items.every((i) => i.description.trim() && i.quantity > 0 && i.unitPrice >= 0);

  const setItem = (idx: number, patch: Partial<LineItem>) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  return (
    <Modal
      title="New invoice"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn"
            disabled={!valid || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : `Create draft · ${money(total, currency)}`}
          </button>
        </>
      }
    >
      <div className="field">
        <label>Family</label>
        <select value={connectionId} onChange={(e) => setConnectionId(e.target.value)}>
          <option value="">Select a family…</option>
          {(clients.data ?? []).map((c) => (
            <option key={c.connectionId} value={c.connectionId}>
              {c.youthSnapshot?.name ?? 'Youth'}
            </option>
          ))}
        </select>
      </div>

      <label className="field-label">Line items</label>
      {items.map((it, idx) => (
        <div key={idx} className="inv__li">
          <input
            placeholder="Description (e.g. Respite care – 4 hours)"
            value={it.description}
            onChange={(e) => setItem(idx, { description: e.target.value })}
          />
          <div className="inv__li-row">
            <input
              type="number"
              min={0}
              step="0.5"
              placeholder="Qty"
              value={it.quantity}
              onChange={(e) => setItem(idx, { quantity: Number(e.target.value) })}
            />
            <input
              type="number"
              min={0}
              step="0.01"
              placeholder="Unit price"
              value={it.unitPrice}
              onChange={(e) => setItem(idx, { unitPrice: Number(e.target.value) })}
            />
            <span className="inv__li-amt">{money(it.quantity * it.unitPrice, currency)}</span>
            {items.length > 1 && (
              <button
                className="inv__li-del"
                onClick={() => setItems((p) => p.filter((_, i) => i !== idx))}
                aria-label="Remove line"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      ))}
      <button className="btn btn--ghost btn--sm" onClick={() => setItems((p) => [...p, blankItem()])}>
        + Add line
      </button>

      <div className="inv__two" style={{ marginTop: 16 }}>
        <div className="field">
          <label>Currency</label>
          <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} />
        </div>
        <div className="field">
          <label>Due date</label>
          <input type="date" min={todayIso()} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>
      </div>

      <div className="field">
        <label>Notes</label>
        <input
          placeholder="Payment terms, instructions…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {create.isError && (
        <p className="error-text">
          {isApiError(create.error) ? create.error.message : 'Could not create the invoice.'}
        </p>
      )}
    </Modal>
  );
}
